import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import 'katex/dist/katex.min.css';
import { InlineMath } from 'react-katex';
import { fetchExamMock } from './mockExamApi';
import globals from '../globals';
import WarningIcon from '@mui/icons-material/Warning';
import RedButton from '../components/redButton';
import BlueButton from '../components/blueButton';
import FileUploadList from '../components/file_upload_list';

// Case-insensitive, whitespace-trimmed comparison for free-text answers.
function answersMatch(given, expected) {
  return String(given ?? '').trim().toLowerCase() === String(expected ?? '').trim().toLowerCase();
}

function ExamQuizPage({
  examData = null,
  onSubmit = null,
  onSaveDraft = null,
  onUploadFile = null,
  initialAnswers = null,
  initialRemainingSeconds = null,
  autoStart = false,
  startButtonText = 'Започни изпита',
  revealAnswers = true,
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [exam, setExam] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedByQuestionId, setSelectedByQuestionId] = useState({});
  // When onUploadFile is provided, entries are uploaded { name, url } objects;
  // otherwise (standalone/mock mode) they are raw File objects.
  const [fileAnswersByQuestionId, setFileAnswersByQuestionId] = useState({});
  const [uploadingByQuestionId, setUploadingByQuestionId] = useState({});
  // Keyed by `${questionId}::${subId}` -> free-text answer for multi_part questions.
  const [subAnswersByKey, setSubAnswersByKey] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [isStarted, setIsStarted] = useState(false);
  // Becomes true once any saved answers have been applied to state, so auto-start
  // (resume) never begins — and never auto-submits — before answers are restored.
  const [hasRestored, setHasRestored] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (examData !== null) {
      setExam(examData);
      setIsLoading(false);
      return;
    }

    fetchExamMock()
      .then((data) => {
        if (!isMounted) return;
        setExam(data);
        setIsLoading(false);
      })
      .catch(() => {
        if (!isMounted) return;
        setHasError(true);
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [examData]);

  // Restore previously autosaved answers so the student can resume.
  useEffect(() => {
    if (!initialAnswers) {
      setHasRestored(true);
      return;
    }
    if (initialAnswers.selected) setSelectedByQuestionId(initialAnswers.selected);
    if (initialAnswers.subAnswers) setSubAnswersByKey(initialAnswers.subAnswers);
    if (initialAnswers.files) setFileAnswersByQuestionId(initialAnswers.files);
    setHasRestored(true);
  }, [initialAnswers]);

  // Resume flow: skip the intro screen and start the countdown automatically, but
  // only after restore has run so we never start/submit with empty answers.
  useEffect(() => {
    if (autoStart && exam && hasRestored && !isStarted && !isSubmitted) {
      handleStartExam();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, exam, hasRestored]);

  useEffect(() => {
    if (!exam || !exam.durationMinutes || remainingSeconds === null || isSubmitted || !isStarted) {
      return;
    }
    if (remainingSeconds <= 0) return;

    const intervalId = window.setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev === null) return prev;
        if (prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [exam, remainingSeconds, isSubmitted]);

  // When the timer runs out, auto-submit whatever the student has entered.
  useEffect(() => {
    if (remainingSeconds === 0 && !isSubmitted) {
      handleSubmit();
    }
  }, [remainingSeconds, isSubmitted]);

  // Debounced autosave: persist a draft ~2s after the last change.
  useEffect(() => {
    if (!onSaveDraft || !isStarted || isSubmitted) return;
    const timeoutId = window.setTimeout(() => {
      onSaveDraft({
        selected: selectedByQuestionId,
        subAnswers: subAnswersByKey,
        files: fileAnswersByQuestionId,
      });
    }, 2000);
    return () => window.clearTimeout(timeoutId);
  }, [onSaveDraft, isStarted, isSubmitted, selectedByQuestionId, subAnswersByKey, fileAnswersByQuestionId]);

  const currentQuestion = useMemo(() => {
    if (!exam || !exam.questions || exam.questions.length === 0) return null;
    return exam.questions[currentIndex] || null;
  }, [exam, currentIndex]);

  const totalQuestions = exam?.questions?.length ?? 0;

  const remainingMinutes = remainingSeconds !== null
    ? Math.floor(remainingSeconds / 60)
    : null;
  const remainingSecondsPart = remainingSeconds !== null
    ? remainingSeconds % 60
    : null;

  function handleStartExam() {
    if (!exam) return;
    if (exam.durationMinutes && remainingSeconds === null) {
      // Prefer the server-computed remaining time (survives refreshes); otherwise
      // start from the full duration.
      const start = typeof initialRemainingSeconds === 'number'
        ? Math.max(0, initialRemainingSeconds)
        : exam.durationMinutes * 60;
      setRemainingSeconds(start);
    }
    setIsStarted(true);
    // On a fresh start, persist an empty draft immediately so started_at (the timer
    // anchor) is set at click time — not on the first debounced autosave. On resume
    // (autoStart) the draft already exists, so we skip this to avoid overwriting it.
    if (!autoStart && onSaveDraft) {
      onSaveDraft({
        selected: selectedByQuestionId,
        subAnswers: subAnswersByKey,
        files: fileAnswersByQuestionId,
      });
    }
  }

  function handleSelect(optionId) {
    if (!currentQuestion || isSubmitted) return;

    setSelectedByQuestionId((prev) => ({
      ...prev,
      [currentQuestion.id]: optionId,
    }));
  }

  async function handleAddFiles(questionId, selectedFiles) {
    if (isSubmitted) return;

    // Standalone/mock mode: append raw File objects, no upload.
    if (!onUploadFile) {
      setFileAnswersByQuestionId((prev) => ({
        ...prev,
        [questionId]: [...(prev[questionId] || []), ...selectedFiles],
      }));
      return;
    }

    // Real exam: upload immediately so answers survive a reload/crash, then append.
    setUploadingByQuestionId((prev) => ({ ...prev, [questionId]: true }));
    const uploaded = [];
    for (const file of selectedFiles) {
      try {
        const result = await onUploadFile(file);
        if (result && result.url) uploaded.push({ name: result.name || file.name, url: result.url });
      } catch { /* skip failed upload */ }
    }
    setFileAnswersByQuestionId((prev) => ({
      ...prev,
      [questionId]: [...(prev[questionId] || []), ...uploaded],
    }));
    setUploadingByQuestionId((prev) => ({ ...prev, [questionId]: false }));
  }

  function handleRemoveFile(questionId, indexToRemove) {
    if (isSubmitted) return;
    setFileAnswersByQuestionId((prev) => ({
      ...prev,
      [questionId]: (prev[questionId] || []).filter((_, index) => index !== indexToRemove),
    }));
  }

  function handleSubAnswerChange(questionId, subId, value) {
    if (isSubmitted) return;
    setSubAnswersByKey((prev) => ({
      ...prev,
      [`${questionId}::${subId}`]: value,
    }));
  }

  function handleNext() {
    if (!exam || !exam.questions) return;
    if (currentIndex < exam.questions.length - 1) setCurrentIndex(currentIndex + 1);
  }

  function handleBack() {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  }

  function handleSubmit() {
    if (!exam || !exam.questions) return;
    if (onSubmit) {
      onSubmit({
        selected: selectedByQuestionId,
        files: fileAnswersByQuestionId,
        subAnswers: subAnswersByKey,
      });
    }
    setIsSubmitted(true);
  }

  const {
    correctCount,
    unansweredCount,
    earnedPoints,
    maxPoints,
  } = useMemo(() => {
    if (!exam || !exam.questions) {
      return {
        correctCount: 0,
        unansweredCount: 0,
        earnedPoints: 0,
        maxPoints: 0,
      };
    }

    let correct = 0;
    let unanswered = 0;
    let earned = 0;
    let max = 0;

    exam.questions.forEach((question) => {
      const points = typeof question.points === 'number' ? question.points : 1;
      max += points;

      if (question.type === 'multi') {
        const subs = question.subquestions || [];
        const correctSubs = subs.filter((sq) =>
          answersMatch(subAnswersByKey[`${question.id}::${sq.id}`], sq.correctAnswer)
        ).length;
        if (!isQuestionAnswered(question.id)) unanswered += 1;
        if (subs.length > 0 && correctSubs === subs.length) correct += 1;
        earned += subs.length > 0 ? Math.round((points * correctSubs) / subs.length) : 0;
        return;
      }

      const selectedChoice = selectedByQuestionId[question.id];
      const files = fileAnswersByQuestionId[question.id];
      const hasFiles = Array.isArray(files) && files.length > 0;
      const isFileType = question.type === 'file';

      const isAnswered = isFileType ? hasFiles : Boolean(selectedChoice);
      if (!isAnswered) {
        unanswered += 1;
        return;
      }

      if (isFileType) {
        correct += 1;
        earned += points;
        return;
      }

      if (selectedChoice === question.correctOptionId) {
        correct += 1;
        earned += points;
      }
    });

    if (!isSubmitted) {
      earned = 0;
    }

    return {
      correctCount: correct,
      unansweredCount: unanswered,
      earnedPoints: earned,
      maxPoints: max,
    };
  }, [exam, selectedByQuestionId, fileAnswersByQuestionId, subAnswersByKey, isSubmitted]);

  function renderWithInlineLatex(text) {
    if (!text || !text.includes('$')) return text;

    const parts = text.split('$');

    return parts.map((part, index) => {
      const isMath = index % 2 === 1;
      if (isMath) {
        return <InlineMath key={index} math={part} />;
      }
      return (
        <React.Fragment key={index}>
          {part}
        </React.Fragment>
      );
    });
  }

  function isQuestionAnswered(questionId) {
    const question = exam?.questions?.find((q) => q.id === questionId);
    if (question?.type === 'multi') {
      const subs = question.subquestions || [];
      return subs.length > 0 &&
        subs.every((sq) => (subAnswersByKey[`${questionId}::${sq.id}`] || '').trim());
    }
    const choice = selectedByQuestionId[questionId];
    const files = fileAnswersByQuestionId[questionId];
    const hasFiles = Array.isArray(files) && files.length > 0;
    return Boolean(choice) || hasFiles;
  }

  if (isLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 6 }}>
          <Typography variant="h5" gutterBottom>
            Зареждане...
          </Typography>
          <LinearProgress />
        </Box>
      </Container>
    );
  }

  if (hasError || !exam) {
    return (
      <Container>
        <Grid container sx={{ py: 6 }}>
          <Typography variant="h5" color="error" gutterBottom>
            Възникна грешка при зареждане на изпита. Моля свържете се с екипа на eAcademiaBg.
          </Typography>
        </Grid>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6 }}>
      <Grid container direction="column" spacing={3}>
        <Grid item>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {exam.title}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {exam.description}
            </Typography>
            {exam.durationMinutes ? (
              <Typography variant="body1" sx={{ mt: 1, color: globals.colors.darkRed }}>
                Продължителност: {exam.durationMinutes} минути, въпроси: {totalQuestions}
              </Typography>
            ) : null}
            {remainingSeconds !== null && isStarted ? (
              <Typography
                variant="h6"
                sx={{ mt: 0.5, fontWeight: 'bold', color: remainingSeconds <= 60 ? 'error' : globals.colors.darkBlue }}
              >
                Оставащо време:{' '}
                {remainingMinutes}:{String(remainingSecondsPart).padStart(2, '0')}
              </Typography>
            ) : null}
          </Box>
        </Grid>

        {!isStarted ? (
          <Grid item>
            <Card elevation={2}>
              <CardContent>
                <Grid container direction="column" spacing={2}>
                  <Grid item>
                    <Typography variant="h6">
                      <WarningIcon sx={{ mb: -0.4, mr: 1 }} />{exam.introTitle || exam.title}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body1" sx={{ color: globals.colors.darkRed, fontWeight: 'bold' }}>
                      {exam.introDescription || exam.description}
                    </Typography>
                  </Grid>
                  {Array.isArray(exam.guidelines) && exam.guidelines.length > 0 ? (
                    <Grid item>
                      <Box component="ul" sx={{ pl: 3, m: 0 }}>
                        {exam.guidelines.map((item) => (
                          <Typography
                            key={item}
                            component="li"
                            variant="body2"
                            sx={{ mb: 0.5, color: globals.colors.text }}
                          >
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    </Grid>
                  ) : null}
                  <Grid item>
                    <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
                      <Grid item>
                        <RedButton
                          text={startButtonText}
                          onClick={handleStartExam}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ) : null}

        {isStarted && totalQuestions > 0 && currentQuestion ? (
          <Grid item>
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} md={3}>
                <Card
                  elevation={1}
                  sx={{
                    position: { xs: 'static', md: 'sticky' },
                    top: { md: 96 },
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Задачи
                    </Typography>
                    <Grid container direction="column" spacing={1}>
                      {exam.questions.map((question, index) => {
                        const isAnswered = isQuestionAnswered(question.id);
                        const isActive = index === currentIndex;

                        return (
                          <Grid item key={question.id}>
                            <Button
                              variant={isAnswered ? 'contained' : isActive ? 'contained' : 'outlined'}
                              size="small"
                              onClick={() => setCurrentIndex(index)}
                              sx={{
                                justifyContent: 'space-between',
                                textTransform: 'none',
                                width: '100%',
                                ...(isAnswered
                                  ? {
                                      bgcolor: globals.colors.darkBlue,
                                      color: '#fff',
                                      borderColor: globals.colors.darkBlue,
                                      '&:hover': {
                                        bgcolor: globals.colors.lightBlue,
                                        color: '#fff',
                                        borderColor: globals.colors.darkBlue,
                                      },
                                    }
                                  : {
                                      bgcolor: isActive ? globals.colors.gray : 'transparent',
                                      color: isActive ? '#fff' : globals.colors.gray,
                                      borderColor: !isActive ? globals.colors.gray : undefined,
                                      '&:hover': {
                                        borderColor: globals.colors.gray,
                                        color: isActive ? '#fff' : globals.colors.gray,
                                      },
                                    }),
                              }}
                            >
                              <Typography variant="body2">
                                Задача {index + 1}
                              </Typography>
                            </Button>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={9}>
                <Card elevation={3}>
                  <CardContent>
                    <Grid container direction="column" spacing={2}>
                      <Grid item>
                        <Grid container justifyContent="space-between" alignItems="center">
                          <Grid item>
                            <Typography variant="subtitle2" color="text.secondary">
                              Задача {currentIndex + 1} от {totalQuestions}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Box sx={{ minWidth: 160 }}>
                              <LinearProgress
                                variant="determinate"
                                value={((currentIndex + 1) / totalQuestions) * 100}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item>
                        {currentQuestion.imageSrc ? (
                          <Box
                            component="img"
                            src={currentQuestion.imageSrc}
                            alt="Илюстрация към въпроса"
                            sx={{
                              maxWidth: '100%',
                              height: 'auto',
                              mb: 2,
                              borderRadius: 1,
                            }}
                          />
                        ) : null}
                        <Typography variant="h6">
                          {renderWithInlineLatex(currentQuestion.text)}
                          {typeof currentQuestion.points === 'number' ? (
                            <Typography
                              component="span"
                              variant="subtitle2"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              ({currentQuestion.points} т.)
                            </Typography>
                          ) : null}
                        </Typography>
                      </Grid>

                      {currentQuestion.type === 'multi' && (
                        <Grid item>
                          <Grid container direction="column" spacing={2}>
                            {(currentQuestion.subquestions || []).map((sq, subIndex) => {
                              const key = `${currentQuestion.id}::${sq.id}`;
                              const given = subAnswersByKey[key] ?? '';
                              const isCorrect = answersMatch(given, sq.correctAnswer);
                              return (
                                <Grid item key={sq.id}>
                                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                                    {subIndex + 1}. {renderWithInlineLatex(sq.text)}
                                  </Typography>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Твоят отговор"
                                    value={given}
                                    disabled={isSubmitted}
                                    onChange={(event) =>
                                      handleSubAnswerChange(currentQuestion.id, sq.id, event.target.value)
                                    }
                                    error={isSubmitted && revealAnswers && !isCorrect}
                                    helperText={
                                      isSubmitted && revealAnswers
                                        ? isCorrect
                                          ? 'Верен отговор'
                                          : `Верен отговор: ${sq.correctAnswer}`
                                        : ''
                                    }
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Grid>
                      )}

                      {currentQuestion.type !== 'file' && currentQuestion.type !== 'multi' && (
                        <Grid item>
                          <RadioGroup
                            value={selectedByQuestionId[currentQuestion.id] ?? ''}
                            onChange={(event) => handleSelect(event.target.value)}
                          >
                            {currentQuestion.options.map((option) => {
                              const isSelected = selectedByQuestionId[currentQuestion.id] === option.id;
                              const isCorrect = option.id === currentQuestion.correctOptionId;

                              let borderColor = 'divider';
                              if (isSubmitted && revealAnswers) {
                                if (isCorrect) borderColor = 'success.main';
                                else if (isSelected && !isCorrect) borderColor = 'error.main';
                              } else if (isSelected) {
                                borderColor = 'primary.main';
                              }

                              return (
                                <Box
                                  key={option.id}
                                  sx={{
                                    mb: 1,
                                    borderRadius: 1,
                                    borderWidth: 1,
                                    borderStyle: 'solid',
                                    borderColor,
                                    '&:hover': {
                                      borderColor: isSubmitted ? borderColor : 'primary.main',
                                      bgcolor: 'action.hover',
                                    },
                                  }}
                                >
                                  <FormControlLabel
                                    value={option.id}
                                    disabled={isSubmitted}
                                    control={<Radio color="primary" />}
                                    label={
                                      <Typography variant="body1">
                                        {renderWithInlineLatex(option.label)}
                                      </Typography>
                                    }
                                    sx={{ pl: 1.5, pr: 1, py: 0.5, width: '100%', m: 0 }}
                                  />
                                </Box>
                              );
                            })}
                          </RadioGroup>
                        </Grid>
                      )}

                      {currentQuestion.type === 'file' && (
                        <Grid item>
                          <Grid container direction="column" spacing={1}>
                            <Grid item>
                              <Typography variant="subtitle2">
                                Качи файлове (pdf, png, jpeg, jpg, heic)
                              </Typography>
                            </Grid>
                            <Grid item>
                              <FileUploadList
                                files={fileAnswersByQuestionId[currentQuestion.id] || []}
                                onAdd={(selected) => handleAddFiles(currentQuestion.id, selected)}
                                onRemove={(index) => handleRemoveFile(currentQuestion.id, index)}
                                getFileKey={(file, index) => `${file.url || file.name}-${index}`}
                                accept=".pdf,.png,.jpeg,.jpg,.heic"
                                addButtonText="Избери файлове"
                                disabled={isSubmitted}
                                uploading={Boolean(uploadingByQuestionId[currentQuestion.id])}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      )}

                      <Grid item>
                        <Grid container justifyContent="space-between" spacing={2}>
                          <Grid item>
                            <BlueButton
                              text="Назад"
                              onClick={handleBack}
                              disabled={currentIndex === 0} />
                          </Grid>
                          <Grid item>
                            <Grid container spacing={1}>
                              <Grid item>
                                {currentIndex < totalQuestions - 1 ? (
                                  <RedButton text="Напред" onClick={handleNext} />
                                ) : (
                                  <RedButton
                                    text="Предай изпита"
                                    onClick={handleSubmit}
                                    disabled={isSubmitted}
                                  />

                                )}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        ) : null}

        {isSubmitted && revealAnswers ? (
          <Grid item>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Резултат от примерния изпит
                </Typography>
                <Typography variant="body1">
                  Верни отговори: {correctCount} от {totalQuestions}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Неотговорени въпроси: {unansweredCount}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Точки: {earnedPoints} от {maxPoints}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ) : null}

        {isSubmitted && !revealAnswers ? (
          <Grid item>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Изпитът е предаден успешно
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Отговорите ти бяха записани и предстои да бъдат оценени от преподавателя.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ) : null}
      </Grid>
    </Container>
  );
}

export default ExamQuizPage;

